import { CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { LabtestOrderDetails } from "../../services/api/LaborderService/types";

const Pagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
}> = ({ currentPage, totalPages, onPageChange, isLoading = false }) => {
    const getVisiblePageNumbers = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i);
        }

        if (currentPage - delta > 2) {
            rangeWithDots.push(1, "...");
        } else {
            rangeWithDots.push(1);
        }

        rangeWithDots.push(...range);

        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push("...", totalPages);
        } else if (totalPages > 1) {
            rangeWithDots.push(totalPages);
        }

        return rangeWithDots;
    };

    if (totalPages <= 1) return null;

    const visiblePages = getVisiblePageNumbers();

    return (
        <div className="flex items-center justify-center gap-2 py-2 px-4 border-t border-gray-200 bg-gray-50">
            {/* Previous Button */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500"
            >
                <ChevronLeft size={16} />
                Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
                {visiblePages.map((page, index) => {
                    if (page === "...") {
                        return (
                            <span key={index} className="px-3 py-2 text-sm text-gray-500">
                                ...
                            </span>
                        );
                    }

                    const pageNumber = page as number;
                    const isCurrentPage = pageNumber === currentPage;

                    return (
                        <button
                            key={pageNumber}
                            onClick={() => onPageChange(pageNumber)}
                            disabled={isLoading}
                            className={`px-3 py-2 text-sm font-medium rounded-md disabled:cursor-not-allowed ${isCurrentPage
                                    ? "bg-blue-600 text-white border border-blue-600"
                                    : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                                }`}
                        >
                            {pageNumber}
                        </button>
                    );
                })}
            </div>

            {/* Next Button */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500"
            >
                Next
                <ChevronRight size={16} />
            </button>
        </div>
    );
};

const LabOrderListingSidebar: React.FC<{
    orders: LabtestOrderDetails[];
    selectedOrder: LabtestOrderDetails | null;
    onOrderSelect: (order: LabtestOrderDetails) => void;
    isLoading: boolean;
    pagination: {
        currentPage: number;
        totalPages: number;
    };
    onPageChange: (page: number) => void;
}> = ({
    orders,
    selectedOrder,
    onOrderSelect,
    isLoading,
    pagination,
    onPageChange,
}) => {
        const formatDate = (dateString: string) => {
            return new Date(dateString).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        };

        return (
            <>
                <div className="w-1/4 border-r border-gray-200 flex flex-col h-full">
                    {/* Orders List */}
                    <div className="flex-1 overflow-y-auto hiddenScrollbar">
                        {isLoading ? (
                            <div className="p-4 text-center text-gray-500">
                                Loading orders...
                            </div>
                        ) : orders.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                No orders found
                            </div>
                        ) : (
                            <div className="space-y-1 bg-gray-50">
                                {orders.map((order) => (
                                    <div
                                        key={order.id}
                                        className={`hover:shadow-xl transition-shadow hover:border hover:border-gray-300 p-3 cursor-pointer hover:bg-white ${selectedOrder?.id === order.id
                                                ? "bg-white shadow-xl border border-gray-300"
                                                : ""
                                            }`}
                                        onClick={() => onOrderSelect(order)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                <CheckCircle size={16} className="text-green-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    {/* <h4 className="font-medium text-sm text-gray-900 truncate capitalize">
                          {order.orderType?.replaceAll("_", " ")}
                        </h4> */}
                                                </div>
                                                <p className="text-xs text-gray-600 mb-1">
                                                    Order ID: {order.orderNumber}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-gray-500">
                                                        {formatDate(order.orderDate)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={onPageChange}
                        isLoading={isLoading}
                    />
                </div>
            </>
        );
    };

export default LabOrderListingSidebar;